/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import _ from 'lodash';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { queryCache, useQuery } from 'react-query';
import { connect, ConnectedProps } from 'react-redux';
import {
  Link as RouterLink,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useResolvedPath,
} from 'react-router';

import { Badge, Box, Tab, Tabs, Typography } from '@mui/material';

import { ClusterStates, DefaultAllowedSourceRange, Endpoints, Infer, ServiceTypeMatrix } from 'shared';

import {
  useUpdateCluster,
  UseUpdateClusterData,
  UseUpdateClusterParams,
} from '../../components/hooks/useUpdateCluster';
import { HUSpin } from '../../components/HUSpin/HUSpin';
import { WarningIcon } from '../../components/svg-icons/WarningIcon';
import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { ClusterApiService } from '../../services/ClusterApiService';
import { RootState } from '../../store';
import { ApiClientError } from '../../types/api/ApiClientError';
import { EntityId } from '../../types/api/EntityId';
import { ClusterCredentialsResponsePayload } from '../../types/cluster/ClusterCredentialsResponsePayload';
import { ClusterStorageResponsePayload } from '../../types/cluster/ClusterStorageResponsePayload';
import { UpdateClusterDetailsParameterIdentification } from '../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { prepareRoutePath } from '../../utils/prepareRoutePath';
import {
  fetchCluster,
  fetchClusterBackupsHistory,
  fetchClusterContinuousBackups,
  fetchClusterDetails,
  fetchClusterLogicalBackups,
  fetchClusterStatus,
  fetchCredentialsOfCluster,
  fetchCredentialsOfClusterCypex,
  fetchDatasetImports,
  fetchDatasets,
  fetchNodeListOfCluster,
  fetchProjectsOfOrganization,
  fetchStorageOfCluster,
} from '../../utils/queryFns';
import { reactToUpdateProject } from '../../utils/reactions';
import { clusterUpgradeHandled } from '../../utils/storageHelpers';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { useProjectPermissions } from '../../utils/useProjectPermissions';
import { ClusterPageTabs, tabToRouteBinding } from './ClusterPage.consts';
import { createClusterDetailsPageStyles } from './ClusterPage.styles';
import { ACLTab } from './components/ACLTab/ACLTab';
import { AdviceTab } from './components/AdviceTab/AdviceTab';
import { ApplicationTab } from './components/ApplicationTab/ApplicationTab';
import { BackupsTab } from './components/BackupsTab/BackupsTab';
import { ClusterDetailsHeader } from './components/ClusterDetailsHeader/ClusterDetailsHeader';
import { ClusterModals } from './components/ClusterModals/ClusterModals';
import { DatasetsTab } from './components/DatasetsTab/DatasetsTab';
import { DetailsTab } from './components/DetailsTab/DetailsTab';
import { ParametersTab } from './components/ParametersTab/ParametersTab';
import { PlanTab } from './components/PlanTab/PlanTab';
import { ReplicasTab } from './components/ReplicasTab/ReplicasTab';
import { SkeletonTabContent } from './components/SkeletonTabContent/SkeletonTabContent';
import { SkeletonTabMenu } from './components/SkeletonTabMenu/SkeletonTabMenu';
import { TAB_CONFIG, TabConfigItem } from './TabConfig';

const mapStateToProps = (state: RootState) => ({
  organizationDetails: state.organizationDetails,
});

const connector = connect(mapStateToProps);
type PathParamsType = {
  organization_id: string;
  project_id: string;
  cluster_id: string;
};
type PropsFromRedux = ConnectedProps<typeof connector>;
type ClusterPageProps = PropsFromRedux;

const ClusterPageComponent = memo<ClusterPageProps>(() => {
  const { t } = useTranslation(Namespaces.Cluster);
  const { i18n } = useTranslation();
  const location = useLocation();
  const baseUrl = useResolvedPath('').pathname;
  const navigate = useNavigate();
  const params = useParams<PathParamsType>();
  const classes = createClusterDetailsPageStyles();
  const [desiredReplicas, setDesiredReplicas] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);
  const [isOpenPermissionsErrorModal, setIsOpenPermissionsErrorModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const deleteFlag = useRef<null | EntityId>(null);

  const organizationId = params.organization_id!;
  const projectId = params.project_id!;
  const clusterId = params.cluster_id!;

  const permissions = useProjectPermissions(organizationId, projectId);

  useEffect(() => {
    if (!permissions.read_cluster) {
      setIsOpenPermissionsErrorModal(true);
    }
  }, [permissions]);

  const identificationData: UpdateClusterDetailsParameterIdentification = {
    organizationId,
    projectId,
    clusterId,
  };

  useEffect(() => {
    deleteFlag.current = null;
    setIsDeleting(false);
    return () => {
      setIsDeleting(false);
      deleteFlag.current = null;
    };
  }, [organizationId, projectId, clusterId]);

  const clusterQuery = useQuery(
    [
      fetchCluster.key,
      {
        ...identificationData,
      },
    ],
    fetchCluster.fn,
    { retry: true, refetchInterval: 10_000 },
  );
  const clusterDetailsQuery = useQuery([fetchClusterDetails.key, { ...identificationData }], fetchClusterDetails.fn);
  const clusterStatusQuery = useQuery(
    [
      fetchClusterStatus.key,
      {
        ...identificationData,
      },
    ],
    fetchClusterStatus.fn,
    { retry: true, refetchInterval: 5_000 },
  );
  const clusterCredentialsQuery = useQuery(
    [
      fetchCredentialsOfCluster.key,
      {
        ...identificationData,
      },
    ],
    fetchCredentialsOfCluster.fn,
    { refetchOnWindowFocus: false, retry: true },
  );
  const clusterStorageQuery = useQuery(
    [
      fetchStorageOfCluster.key,
      {
        ...identificationData,
      },
    ],
    fetchStorageOfCluster.fn,
    { refetchOnWindowFocus: false, retry: true },
  );
  const clusterNodesQuery = useQuery(
    [
      fetchNodeListOfCluster.key,
      {
        ...identificationData,
      },
    ],
    fetchNodeListOfCluster.fn,
    { refetchOnWindowFocus: false, retry: true, refetchInterval: 15_000 },
  );
  const clusterContinuousBackupsQuery = useQuery(
    [
      fetchClusterContinuousBackups.key,
      {
        ...identificationData,
      },
    ],
    fetchClusterContinuousBackups.fn,
    { refetchOnWindowFocus: false, retry: true },
  );
  const clusterLogicalBackupsQuery = useQuery(
    [
      fetchClusterLogicalBackups.key,
      {
        ...identificationData,
      },
    ],
    fetchClusterLogicalBackups.fn,
    { refetchOnWindowFocus: false, retry: true },
  );
  const clusterBackupsHistoryQuery = useQuery(
    [
      fetchClusterBackupsHistory.key,
      {
        ...identificationData,
      },
    ],
    fetchClusterBackupsHistory.fn,
    { refetchOnWindowFocus: false },
  );
  const projectsQuery = useQuery(
    [fetchProjectsOfOrganization.key, { ...identificationData }],
    fetchProjectsOfOrganization.fn,
  );

  const cluster = useMemo(
    () => clusterQuery.data?.payload ?? ({} as Infer<typeof Endpoints.GetCluster>['Response']['200']),
    [clusterQuery.data],
  );
  const clusterDetails = useMemo(
    () => clusterDetailsQuery.data?.payload ?? ({} as Infer<typeof Endpoints.GetClusterDetails>['Response']['200']),
    [clusterDetailsQuery.data],
  );
  const showAclWarning = useMemo(
    () =>
      clusterDetails?.allowedSourceRanges?.length === 1 &&
      clusterDetails?.allowedSourceRanges?.[0] === DefaultAllowedSourceRange,
    [clusterDetails],
  );

  const status = useMemo(() => clusterStatusQuery.data?.payload?.status, [clusterStatusQuery.data]);
  const credentials = useMemo(
    () => clusterCredentialsQuery.data?.payload ?? ({} as ClusterCredentialsResponsePayload),
    [clusterCredentialsQuery.data],
  );
  const storage = useMemo(
    () => clusterStorageQuery.data?.payload ?? ({} as ClusterStorageResponsePayload),
    [clusterStorageQuery.data],
  );
  const nodes = useMemo(() => clusterNodesQuery.data?.payload || [], [clusterNodesQuery.data]);
  const logicalBackups = useMemo(
    () => clusterLogicalBackupsQuery.data?.payload || [],
    [clusterLogicalBackupsQuery.data],
  );
  const continuousBackups = useMemo(
    () => clusterContinuousBackupsQuery.data?.data.payload || [],
    [clusterContinuousBackupsQuery.data],
  );
  const backupsHistory = useMemo(
    () => clusterBackupsHistoryQuery.data?.payload || [],
    [clusterBackupsHistoryQuery.data],
  );
  const relativeProjects = useMemo(() => projectsQuery.data?.data.payload || [], [projectsQuery.data]);

  const isFetchingStorage = useMemo(() => clusterStorageQuery.status === 'loading', [clusterStorageQuery.status]);
  const isFetchingNodes = useMemo(
    () => clusterNodesQuery.status === 'loading' || status === ClusterStates.Creating,
    [clusterNodesQuery.status, status],
  );
  const isFetchingLogicalBackups = useMemo(
    () => clusterLogicalBackupsQuery.status === 'loading',
    [clusterLogicalBackupsQuery.status],
  );

  const isSpinning = useMemo(
    () =>
      status === ClusterStates.Updating || status === ClusterStates.Importing || status === ClusterStates.Unavailable,
    [status],
  );

  const isClusterUnavailable = useMemo(() => status === ClusterStates.Unavailable, [status]);

  const clusterCredentialsCypexQuery = useQuery(
    [
      fetchCredentialsOfClusterCypex.key,
      {
        ...identificationData,
      },
    ],
    fetchCredentialsOfClusterCypex.fn,
    { refetchOnWindowFocus: false, retry: true, enabled: cluster.type === 'cypex' },
  );

  const clusterDatasetsQuery = useQuery(
    [
      fetchDatasetImports.key,
      {
        ...identificationData,
      },
    ],
    fetchDatasetImports.fn,
    { refetchInterval: 5_000 },
  );

  const dataSetsCategoiesQuery = useQuery([fetchDatasets.key, { language: i18n.language }], fetchDatasets.fn);

  const credentialsCypex = useMemo(
    () =>
      clusterCredentialsCypexQuery.data?.payload ??
      ({} as Pick<ClusterCredentialsResponsePayload, 'host' | 'port' | 'username' | 'password'>),
    [clusterCredentialsCypexQuery.data],
  );

  const dataSetsCategories = useMemo(
    () => (dataSetsCategoiesQuery.data?.payload ?? []).filter(i => i.datasets.length > 0),
    [dataSetsCategoiesQuery.data],
  );

  const datasets = useMemo(() => clusterDatasetsQuery.data?.payload, [clusterDatasetsQuery.data]);

  const isDatasetsDataSuitable = useMemo(
    () => datasets?.length || dataSetsCategories.length,
    [dataSetsCategories.length, datasets],
  );

  const shouldHideDatasetTab = useMemo(() => !isDatasetsDataSuitable, [isDatasetsDataSuitable]);

  const clusterName = cluster.name;
  useDocumentTitle(clusterName);
  const [updateData] = useUpdateCluster();

  const onUpdateData = (
    data: UseUpdateClusterData['data'],
    params: UseUpdateClusterParams = {},
    throwOnError?: boolean,
  ) => {
    return updateData({ ...identificationData, data, ...params }, { throwOnError });
  };

  const deleteCluster = useCallback(async () => {
    deleteFlag.current = clusterId!;
    setIsDeleting(true);
    try {
      if (projectId && clusterId) {
        await ClusterApiService.deleteCluster({
          organizationId,
          projectId,
          clusterId,
        });
        clusterUpgradeHandled.remove(clusterId);
        queryCache.removeQueries(({ queryKey }) => _.get(queryKey[1], 'clusterId') === clusterId);
        await reactToUpdateProject({
          organizationId,
          projectId,
        });
        deleteFlag.current === clusterId &&
          void navigate(
            prepareRoutePath(BasicRoutes.PROJECT_PAGE, {
              ':organization_id': organizationId,
              ':project_id': projectId,
            }),
          );
      }
    } catch (e) {
      const error = e as ApiClientError;
      console.error(error);
      setIsOpenErrorModal(true);
    }
  }, [clusterId, navigate, organizationId, projectId]);

  const getPathForTab = (tab: ClusterPageTabs) => {
    return `${baseUrl}/${tabToRouteBinding[tab]}`;
  };

  const onOkPermissionsError = () => {
    setIsOpenPermissionsErrorModal(false);
    void navigate(-1);
  };

  useEffect(() => {
    if (clusterQuery.data) {
      setIsLoading(false);
    }
  }, [clusterQuery.data, clusterStatusQuery.data]);

  const renderTabContent = () => {
    if (isLoading) {
      return <SkeletonTabContent />;
    }

    return (
      <Routes>
        <Route
          index
          element={
            ServiceTypeMatrix.displayApplication[cluster.type] ? (
              <HUSpin spinning={isSpinning} delay={50}>
                <ApplicationTab serviceId={identificationData.clusterId} serviceType={cluster.type} />
              </HUSpin>
            ) : ServiceTypeMatrix.displayClusterCredentials[cluster.type] ? (
              <Navigate to={tabToRouteBinding[ClusterPageTabs.Overview]} replace />
            ) : (
              <Navigate to={tabToRouteBinding[ClusterPageTabs.Application]} replace />
            )
          }
        />
        <Route
          path={tabToRouteBinding[ClusterPageTabs.Application]}
          element={
            ServiceTypeMatrix.displayApplication[cluster.type] ? (
              <HUSpin spinning={isSpinning} delay={50}>
                <ApplicationTab serviceId={identificationData.clusterId} serviceType={cluster.type} />
              </HUSpin>
            ) : (
              <Navigate to={tabToRouteBinding[ClusterPageTabs.Overview]} replace />
            )
          }
        />
        <Route
          path={tabToRouteBinding[ClusterPageTabs.Overview]}
          element={
            ServiceTypeMatrix.displayClusterCredentials[cluster.type] ? (
              <>
                <DetailsTab
                  serviceType={cluster.type}
                  status={status}
                  credentials={credentials}
                  credentialsCypex={credentialsCypex}
                  storage={storage}
                  isFetchingStorage={isFetchingStorage}
                  nodes={nodes}
                  isFetchingNodes={isFetchingNodes}
                  backupsHistory={backupsHistory}
                  updateDesiredReplicas={replicas => {
                    setDesiredReplicas(replicas);
                  }}
                  identificationData={identificationData}
                  updateData={onUpdateData}
                />
                {storage && storage.filesystem === 'StorageError' && (
                  <Typography component="div" classes={{ root: classes.storageErrorAnnotation }}>
                    <Trans
                      i18nKey={`${Namespaces.Cluster}:details.storage.error.annotation`}
                      components={{
                        nodeLink: (
                          <Typography
                            classes={{ root: classes.nodeLink }}
                            component={RouterLink}
                            to={getPathForTab(ClusterPageTabs.Replicas)}
                          />
                        ),
                      }}
                    />
                  </Typography>
                )}
              </>
            ) : (
              <Navigate to={tabToRouteBinding[ClusterPageTabs.Application]} replace />
            )
          }
        />
        <Route
          path={tabToRouteBinding[ClusterPageTabs.Plan]}
          element={
            <PlanTab
              cluster={cluster}
              maintenanceWindows={clusterDetails.maintenanceWindows || []}
              status={status}
              identificationData={identificationData}
            />
          }
        />
        <Route
          path={tabToRouteBinding[ClusterPageTabs.Advice]}
          element={
            !ServiceTypeMatrix.displayAdvice[cluster.type] ? (
              <Navigate to={getPathForTab(ClusterPageTabs.Overview)} replace />
            ) : (
              <AdviceTab identificationData={identificationData} status={status} />
            )
          }
        />
        <Route
          path={tabToRouteBinding[ClusterPageTabs.Parameters]}
          element={
            !ServiceTypeMatrix.displayParameters[cluster.type] ? (
              <Navigate
                to={{
                  pathname: getPathForTab(ClusterPageTabs.Overview),
                }}
              />
            ) : (
              <ParametersTab identificationData={identificationData} updateData={onUpdateData} />
            )
          }
        />
        <Route
          path={tabToRouteBinding[ClusterPageTabs.ACL]}
          element={
            !ServiceTypeMatrix.displayAcl[cluster.type] ? (
              <Navigate
                to={{
                  pathname: getPathForTab(ClusterPageTabs.Overview),
                }}
              />
            ) : (
              <HUSpin spinning={isSpinning} delay={50}>
                <ACLTab updateData={onUpdateData} identificationData={identificationData} />
              </HUSpin>
            )
          }
        />
        <Route
          path={tabToRouteBinding[ClusterPageTabs.Replicas]}
          element={
            !ServiceTypeMatrix.displayReplicas[cluster.type] ? (
              <Navigate
                to={{
                  pathname: getPathForTab(ClusterPageTabs.Overview),
                }}
              />
            ) : (
              <ReplicasTab
                identificationData={identificationData}
                cluster={cluster}
                status={status}
                nodes={nodes}
                isLoadingNodes={isFetchingNodes}
              />
            )
          }
        />
        <Route
          path={tabToRouteBinding[ClusterPageTabs.Backups]}
          element={
            !ServiceTypeMatrix.displayBackups[cluster.type] ? (
              <Navigate
                to={{
                  pathname: getPathForTab(ClusterPageTabs.Overview),
                }}
              />
            ) : (
              <BackupsTab
                identificationData={identificationData}
                isSpinning={isSpinning}
                continuousBackups={continuousBackups}
                logicalBackups={logicalBackups}
                isFetchingLogicalBackups={isFetchingLogicalBackups}
              />
            )
          }
        />
        <Route
          path={tabToRouteBinding[ClusterPageTabs.Datasets]}
          element={
            !ServiceTypeMatrix.displayDatasets[cluster.type] || shouldHideDatasetTab ? (
              <Navigate
                to={{
                  pathname: getPathForTab(ClusterPageTabs.Overview),
                }}
              />
            ) : (
              <DatasetsTab
                identificationData={identificationData}
                isClusterUnavailable={isClusterUnavailable}
                datasets={datasets}
                datasetsCategories={dataSetsCategories}
                clusterStatus={status}
              />
            )
          }
        />
      </Routes>
    );
  };

  const renderTabMenu = () => {
    if (isLoading) {
      return <SkeletonTabMenu />;
    }

    return (
      <Tabs
        orientation="vertical"
        aria-label="cluster details tabs"
        indicatorColor="primary"
        scrollButtons={false}
        value={location.pathname}
        className={classes.verticalTabs}
      >
        {TAB_CONFIG.filter(tab => tab.show(cluster)).map((tab: TabConfigItem) => (
          <Tab
            key={tab.key}
            label={tab.label(t)}
            {...(tab.icon
              ? {
                  icon: (
                    <Badge
                      invisible={tab.key !== 'acl' || !showAclWarning}
                      badgeContent={<WarningIcon color="warning" />}
                    >
                      {tab.icon}
                    </Badge>
                  ),
                }
              : {})}
            component={NavLink}
            value={tab.value(baseUrl, getPathForTab)}
            to={tab.to(tabToRouteBinding, getPathForTab)}
            data-cy={tab.dataCy}
            className={
              tab.className ? tab.className(tab.key === 'datasets' ? shouldHideDatasetTab : false, cx) : undefined
            }
          />
        ))}
      </Tabs>
    );
  };

  return (
    <Box className={classes.root}>
      <HUSpin spinning={isDeleting} indicator={null} className={classes.clusterDetailsContainer}>
        <ClusterDetailsHeader
          updateData={onUpdateData}
          desiredReplicas={desiredReplicas}
          service={cluster}
          status={status}
          storage={storage}
          isLoadingDetails={clusterDetailsQuery.status === 'loading'}
          maintenanceWindows={clusterDetails.maintenanceWindows}
          identificationData={identificationData}
          deleteCluster={deleteCluster}
          relativeProjects={relativeProjects}
          isSpinning={isSpinning}
          ids={{
            cluster: clusterId,
            project: projectId,
            organization: organizationId,
          }}
        />
      </HUSpin>
      <Box className={classes.mainRow}>
        <Box className={classes.tabMenu}>{renderTabMenu()}</Box>
        <Box className={classes.content}>{renderTabContent()}</Box>
      </Box>
      <ClusterModals
        isOpenErrorModal={isOpenErrorModal}
        isOpenPermissionsErrorModal={isOpenPermissionsErrorModal}
        onCloseErrorModal={() => setIsOpenErrorModal(false)}
        onClosePermissionsErrorModal={onOkPermissionsError}
      />
    </Box>
  );
});

export const ClusterPage = connector(ClusterPageComponent);
